import { useEffect, useState } from "react";
import { Grid } from "@material-ui/core";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import InputText from "components/_refactor/text/input";
import useStructureQueries from "hooks/useStructureQueries";
import MaskAndValid from "utils/maskAndValid";

const getSelectedDefaultValue = (selected, options, idKey) => {
  if (
    selected === null ||
    selected === undefined ||
    !options ||
    options.length === 0
  )
    return null;
  const selectedNormalized =
    typeof selected === "string" ? parseInt(selected, 10) : selected;
  return options.find((option) => option[idKey] === selectedNormalized);
};

export default function EconomicGroupStoreFields({
  onFieldsChange,
  disableAll,
  onFieldsLoad,
  requiredFields,
  clearFilters,
  defaultGroupName,
}) {
  const snackbar = useSnackbar();
  const { getEconomicGroups, getStoreUser } = useStructureQueries();

  const [groupNameList, setGroupNameList] = useState([]);
  const [socialNameList, setSocialNameList] = useState([]);

  const [selectedGroupName, setSelectedGroupName] = useState(null);
  const [selectedSocialName, setSelectedSocialName] = useState(null);
  const [cnpj, setCnpj] = useState("");

  const [loadingFields, setLoadingFields] = useState([]);

  const isLoading = (field) => loadingFields?.includes(field);
  const isLoadingGroupName = isLoading("groupName");
  const isLoadingSocialName = isLoading("socialName");

  const handleLoadingFields = (fields) => {
    setLoadingFields(fields);
    if (onFieldsLoad) {
      onFieldsLoad(fields);
    }
  };

  const isDisabled = (field) => disableAll || isLoading(field);
  const isRequired = (field) => requiredFields?.includes(field);

  async function fetchEconomicGroupList() {
    handleLoadingFields(["groupName"]);

    try {
      const result = await getEconomicGroups({});
      const list = result?.data ?? [];
      setGroupNameList(list);
      if (list.length === 1) {
        setSelectedGroupName(list[0]);
      }
    } catch (error) {
      snackbar.showMessage(
        "Ocorreu um erro ao buscar grupos econômicos. Verifique os parâmetros informados.",
        { severity: "error" }
      );
    } finally {
      handleLoadingFields([]);
    }
  }

  async function fetchStoreList(page = 0) {
    if (!selectedGroupName) {
      setSocialNameList([]);
      return;
    }

    if (page === 0) {
      handleLoadingFields(["socialName"]);
    }

    try {
      const params = {
        ...(selectedGroupName
          ? {
              idGrupoEconomico: selectedGroupName.Id,
            }
          : {}),
        page,
        pageSize: 1000,
      };

      const result = await getStoreUser(params);
      const value = result?.data ?? { ListLojas: [], TotalDePaginas: 0 };

      if (page === 0) {
        setSocialNameList(value.ListLojas ?? []);
      } else {
        setSocialNameList((prev) => [
          ...prev,
          ...(value.ListLojas ?? []),
        ]);
      }

      if ((value.TotalDePaginas ?? 0) > page) {
        await fetchStoreList(page + 1);
      } else {
        handleLoadingFields([]);
      }
    } catch (error) {
      handleLoadingFields([]);
      snackbar.showMessage(
        "Ocorreu um erro ao buscar lojas. Verifique os parâmetros informados.",
        { severity: "error" }
      );
    }
  }

  const onGroupNameChange = (newValue) => {
    if (newValue === "all") {
      setSelectedGroupName(null);
      setSelectedSocialName(null);
      setCnpj("");
      setSocialNameList([]);
    } else {
      setSelectedGroupName(newValue);
      setSelectedSocialName(null);
      setCnpj("");
    }
  };

  const onSocialNameChange = (newValue) => {
    if (newValue === "all") {
      setCnpj("");
      setSelectedSocialName(null);
    } else {
      setCnpj(MaskAndValid.maskCNPJ(newValue?.CNPJ || ""));
      setSelectedSocialName(newValue);
    }
  };

  const onCnpjChange = (e) => {
    setCnpj(MaskAndValid.maskCNPJ(e.target.value));
  };

  useEffect(() => {
    fetchEconomicGroupList();
  }, []);

  useEffect(() => {
    fetchStoreList();
  }, [selectedGroupName]);

  useEffect(() => {
    onFieldsChange({
      idGrupoEconomico: selectedGroupName?.Id || null,
      idPessoa: selectedSocialName?.Id || null,
      cnpj: cnpj.replace(/\D/g, "") || null,
      groupName: selectedGroupName || null,
      socialName: selectedSocialName || null,
    });
  }, [selectedGroupName, selectedSocialName, cnpj, onFieldsChange]);

  useEffect(() => {
    if (clearFilters) {
      handleLoadingFields(["groupName"]);

      setSelectedGroupName(null);
      setSelectedSocialName(null);
      setCnpj("");
      setSocialNameList([]);

      const resetFilters = async () => {
        await fetchEconomicGroupList();
        handleLoadingFields([]);
      };

      resetFilters();
    }
  }, [clearFilters]);

  useEffect(() => {
    if (defaultGroupName && groupNameList.length > 0) {
      const defaultGroup = getSelectedDefaultValue(
        defaultGroupName,
        groupNameList,
        "Id"
      );
      if (defaultGroup) {
        setSelectedGroupName(defaultGroup);
      }
    }
  }, [defaultGroupName, groupNameList]);

  return (
    <>
      <Grid item xs={3} style={{ marginBottom: "0" }}>
        <AutoCompleteField
          label="Nome do Grupo"
          onChange={onGroupNameChange}
          options={groupNameList}
          disabled={isDisabled("groupName") || groupNameList.length === 1}
          loading={isLoadingGroupName}
          value={
            selectedGroupName ??
            (groupNameList?.length === 1 ? groupNameList[0] : null)
          }
          optionIdKey="Id"
          required={isRequired("groupName")}
        />
      </Grid>

      <Grid item xs={6} style={{ marginBottom: "0" }}>
        <AutoCompleteField
          label="Razão Social / Loja"
          onChange={onSocialNameChange}
          options={socialNameList}
          disabled={isDisabled("socialName") || !selectedGroupName}
          loading={isLoadingGroupName || isLoadingSocialName}
          value={selectedSocialName}
          optionIdKey="Id"
          required={isRequired("socialName")}
        />
      </Grid>

      <Grid item xs={3}>
        <InputText
          label="CNPJ"
          placeholder="00.000.000/0000-00"
          value={cnpj}
          onChange={onCnpjChange}
          disabled={
            isDisabled("cnpj") || isLoadingSocialName || isLoadingGroupName
          }
          required={isRequired("cnpj")}
        />
      </Grid>
    </>
  );
}
